/**
 * The tuning table. Every number the design document (docs/game-design.md v0.8)
 * states lives here and nowhere else; tests pin the ones the document quotes.
 * Units: metres, seconds, kilograms, ticks (30 per second).
 */
export const TICK_RATE = 30;
export const DT = 1 / TICK_RATE;

export const tuning = {
  limbs: {
    /**
     * Peak tip speed (m/s) a throw may reach: the throw lasts at least as long
     * as its path needs at this speed, so a long hook is not faster than a cross.
     */
    arm: {
      mass: 2.5,
      tipSpeedTap: 7.5,
      tipSpeedCharged: 11.5,
      reach: 0.95,
      windupTap: 4,
      windupCharged: 12,
      /** Fewest throw ticks; otherwise the path's length at the tip-speed limit decides. */
      throwMin: 2,
      recovery: 8,
    },
    leg: {
      mass: 3.5,
      tipSpeedTap: 9,
      tipSpeedCharged: 13,
      reach: 1.25,
      windupTap: 6,
      windupCharged: 18,
      throwMin: 3,
      recovery: 10,
    },
  },
  /**
   * Where along its reach each arc carries power (fraction of the limb's reach
   * from shoulder or hip to the contact). A straight needs the limb nearly
   * extended, so it jams up close; a hook and an uppercut are thrown with a
   * bent arm and peak much closer; a roundhouse lands with the shin at kicking
   * range and with the thigh (jammed) inside it; the rising leg arc is the
   * knee-range strike. Power ramps from `jammedFactor` at `jammedBelow` to
   * full at `peakFrom`: a straight builds toward full extension, so thrown at
   * a target too close it lands early and light even though the fist is fast.
   * Indexed by arc: straight, around, rising.
   */
  extension: {
    arm: [
      {
        jammedBelow: 0.55,
        peakFrom: 0.8,
        jammedFactor: 0.5,
        overReachAbove: 0.97,
        overReachFactor: 0.7,
      },
      {
        jammedBelow: 0.3,
        peakFrom: 0.45,
        jammedFactor: 0.6,
        overReachAbove: 0.9,
        overReachFactor: 0.6,
      },
      {
        jammedBelow: 0.25,
        peakFrom: 0.4,
        jammedFactor: 0.6,
        overReachAbove: 0.85,
        overReachFactor: 0.6,
      },
    ],
    // A jammed kick lands with the knee or thigh, which moves at under half the foot's speed.
    leg: [
      {
        jammedBelow: 0.55,
        peakFrom: 0.75,
        jammedFactor: 0.35,
        overReachAbove: 0.97,
        overReachFactor: 0.7,
      },
      {
        jammedBelow: 0.45,
        peakFrom: 0.65,
        jammedFactor: 0.35,
        overReachAbove: 0.97,
        overReachFactor: 0.7,
      },
      {
        jammedBelow: 0.2,
        peakFrom: 0.35,
        jammedFactor: 0.6,
        overReachAbove: 0.8,
        overReachFactor: 0.6,
      },
    ],
  },
  /**
   * Fighting stance (design §4.4). Stance +1 is orthodox (left foot and hand
   * lead), −1 southpaw. The lead foot stands `halfStride` ahead of the hips and
   * the rear foot as far behind; shoulders and hips are bladed so the lead side
   * is forward. Rest hands are a guard: lead hand out at shoulder height, rear
   * hand at the chin (relative to the shoulder height, fighter space).
   */
  stance: {
    halfStride: 0.24,
    footX: 0.15,
    shoulderZ: 0.09,
    hipZ: 0.05,
    switchTicks: 10,
    switchStamina: 4,
    leadHand: { x: 0.14, y: -0.2, z: 0.3 },
    rearHand: { x: 0.17, y: -0.15, z: 0.12 },
  },
  /**
   * Kinetic chain (design §5.3). Power comes from how far the trunk turns
   * behind a strike: `twist` is that turn in blade units, per limb kind and
   * arc, for a lead and a rear limb. A jab barely turns (0.4); a cross turns
   * through (1.8); a lead hook turns almost as far as a rear one, so it hits
   * almost as hard. The effective-mass factor is `base + gain × twist /
   * fullTwist`, and the same twist turns the rendered shoulders and hips.
   * A lead limb loads quicker; a rear limb coils further and costs more.
   */
  chain: {
    base: 0.8,
    gain: 0.5,
    fullTwist: 1.8,
    twist: {
      arm: [
        [0.4, 1.8],
        [1.4, 1.8],
        [0.8, 1.4],
      ],
      leg: [
        [0.4, 1],
        [0.8, 1.8],
        [0.4, 1],
      ],
    },
    leadWindup: 0.7,
    rearWindup: 1.15,
    /** Peak tip speed factor: the rear side has a little further to accelerate. */
    leadSpeed: 0.9,
    rearSpeed: 1,
    leadCost: 0.85,
    rearCost: 1.15,
    /** Extra retraction of a rear limb while it loads (the hip coils), metres. */
    rearLoad: 0.1,
    /**
     * Fastest the trunk turns, blade units per tick: a combo that goes from a
     * cross into a hook unwinds the body instead of snapping it round.
     */
    twistRate: 0.6,
  },
  /**
   * Target zones (design §5.1, §6): heights above the floor on a standing
   * fighter. Keys pick the zone; a strike goes for that height on the
   * opponent the look points at. Heights, not parts: a crouched head is below
   * the head zone and in the body zone.
   */
  zones: { head: 1.65, body: 1.05, legs: 0.62 },
  /**
   * Which opponent a strike goes for: the one nearest the look's heading
   * within `cone` (a cosine, ≈ 25°) and within the limb's reach plus `beyond`.
   */
  aim: { cone: 0.9, beyond: 0.6 },
  /**
   * Combos (design §5.2): one throw at a time; a press made while a throw is
   * on waits its turn, up to `maxChain` strikes in a row, and the next link
   * starts when the previous throw ends.
   */
  combo: { maxChain: 3 },
  /**
   * Builds (design §4.5). Speed and strength live on each fighter; a hit's
   * damage is divided by the target's toughness, 1 + (strength − 1) × `toughness`.
   */
  build: { toughness: 0.6 },
  /**
   * Stepping into a strike. The limb keeps its own speed (the throw covers its
   * planned distance, so a closer target is met sooner) and the body adds at
   * most `bodyCarry` m/s of its motion and `bodyFactor` of mass behind it.
   */
  bodyFactor: 1.1,
  bodyCarry: 0.4,
  k: 0.085,
  vulnerability: { head: 1.6, torso: 1.0, upperArm: 0.8, forearm: 0.8, thigh: 0.9, shin: 0.9 },
  counterHit: 1.5,
  whiffRecoveryFactor: 1.3,
  chargeMaxTicks: 12,
  /** Body mass the shove impulse is spread over, and a feel multiplier. */
  bodyMass: 70,
  shoveScale: 2.5,
  /** Follow-through past the aim point when nothing is hit. */
  followThrough: 1.15,
  /** Lateral / vertical offset of the arc control point, metres. */
  arcOffset: 0.45,
  /** How high a kicking foot chambers during the wind-up (knee up), metres; rising kicks less. */
  chamber: 0.3,
  staggerTicks: 8,
  /** Minimum closing speed (m/s) for a contact to count as a hit at all. */
  minClosingSpeed: 1.0,
  guard: {
    armSpeed: 6,
    shieldWidth: 0.5,
    absorbBase: 0.5,
    absorbSquare: 0.35,
    /** Share of the unabsorbed damage the guarding segment (arm or checking shin) takes. */
    forearmWear: 0.1,
    /** A kick blocked on the arms wears them this many times faster (forearms break on kicks; shins check). */
    kickWear: 4,
    staminaPerEnergy: 0.08,
    collapseEnergy: 150,
    /** Stamina still regenerates while covering, at this fraction: patience is a strategy. */
    regenFactor: 0.5,
    /**
     * A blocked strike costs its thrower this fraction of its throw cost again
     * (UFC 5 prices a blocked strike at ≈ 1.2× a landed one).
     */
    attackerJolt: 0.2,
    /** Extra recovery for a blocked strike: the defender's window to counter. */
    blockedRecovery: 4,
    /**
     * Fist height when covering the body (Space + F): the forearms hang from
     * there over the ribs and the belly. Covering the head puts them at the
     * temples; covering the legs checks.
     */
    bodyHands: 1.1,
  },
  /**
   * Checking a kick: covering the legs (Space + Shift) lifts the lead knee so
   * the shin meets a low kick. The kicker's shin takes `recoil` of the kick's damage.
   */
  check: {
    foot: { x: 0.1, y: 0.55, z: 0.2 },
    footSpeed: 6,
    moveFactor: 0.3,
    recoil: 0.4,
  },
  /**
   * Parry: a timed push that deflects. It stays open long enough to be played
   * over 65–90 ms of latency (8 ticks ≈ 267 ms), a success refunds its whole
   * cost, and the deflected thrower pays `attackerJolt` × the throw's cost.
   */
  parry: {
    push: 0.4,
    ticks: 8,
    stamina: 10,
    refund: 10,
    attackerRecoveryPenalty: 8,
    attackerJolt: 1.5,
  },
  initiativeTicks: 6,
  /**
   * Lag compensation: a strike is resolved against opponents' positions and
   * facings as the attacker saw them, at most this many ticks in the past
   * (10 ticks ≈ 333 ms). The floor with no network is ~6 ticks (100 ms
   * interpolation, snapshot cadence, one queued input), so this covers a
   * round trip up to ~150 ms. Guard, parry, crouch and slip are never rewound.
   */
  lagCompensation: { maxRewindTicks: 10 },
  stamina: {
    max: 100,
    regen: 12,
    regenStill: 18,
    step: 20,
    feint: 8,
    exhaustedSpeed: 0.6,
    strikeTapArm: 6,
    strikeChargedArm: 12,
    strikeTapLeg: 12,
    strikeChargedLeg: 22,
    /** A strike that meets nothing costs this fraction of its throw cost again (≈ 1.8× in total). */
    whiff: 0.8,
  },
  hp: {
    head: 100,
    torso: 160,
    upperArm: 60,
    forearm: 45,
    thigh: 80,
    shin: 55,
    injuredBelow: 0.3,
    eliminateBelowTotal: 0.25,
  },
  injury: {
    upperArmSpeed: 0.7,
    upperArmMass: 0.8,
    forearmSpeed: 0.8,
    thighMoveSpeed: 0.7,
    thighKickSpeed: 0.7,
    shinKickSpeed: 0.8,
    limpSpeed: 0.4,
    dazedParryFactor: 0.5,
    torsoRegenFactor: 0.5,
  },
  cage: {
    /**
     * Apothem (centre to wall) by fighter count, metres, as [fighters, r0]
     * points in ascending order; counts in between are interpolated linearly.
     */
    r0: [
      [3, 8],
      [5, 10],
      [10, 14],
      [15, 16],
      [20, 20],
      [50, 30],
    ] as readonly (readonly [number, number])[],
    /** 45 s × 3/5: the cadence scaled with the 3-minute clock, so six closes still fit. */
    shrinkEverySeconds: 27,
    shrinkFactor: 0.8,
    rMin: 2.5,
    warningSeconds: 5,
    moveOverSeconds: 3,
    crushIntervalSeconds: 0.5,
    crushDamage: 2,
    sides: 8,
  },
  match: { seconds: 180, countdownSeconds: 10 },
  movement: {
    walk: 4,
    strafe: 3,
    back: 2.5,
    duck: 2,
    duckDrop: 0.5,
    duckTicks: 4,
    /** Slip: how far the upper body shifts sideways, how far it dips, and how long it takes. */
    leanSway: 0.26,
    leanDrop: 0.08,
    leanTicks: 5,
    /** Walking speed multiplier while slipped past half. */
    leanMove: 0.7,
    step: 2,
    stepTicks: 9,
    /** Fighter body capsule radius and standing height. */
    radius: 0.35,
    height: 1.8,
    /** How fast facing catches up with look yaw, radians per second. */
    turnRate: 12,
    /** Shove: fraction of a walking fighter's velocity transferred to a shoved fighter. */
    shoveFactor: 0.6,
  },
  body: {
    /** Metres, local space: shoulders and hips are mirrored on x. */
    shoulderY: 1.45,
    shoulderX: 0.22,
    /** Hips in a fighting stance: knees bent, so a staggered foot is reachable. */
    hipY: 0.9,
    hipX: 0.12,
    headY: 1.65,
    headRadius: 0.13,
    torsoRadius: 0.2,
    upperArmLength: 0.3,
    forearmLength: 0.32,
    armRadius: 0.06,
    fistRadius: 0.08,
    thighLength: 0.46,
    shinLength: 0.5,
    legRadius: 0.08,
    footRadius: 0.12,
    /** Guard hand offsets from the aim line, fighter space. */
    guardHand: { x: 0.13, forward: 0.18 },
  },
} as const;

export type Tuning = typeof tuning;

/**
 * Manual version for logic changes plus a hash of the tuning table, so a
 * client built from another table or another sim is refused at join.
 */
export const SIM_VERSION = "m2.5-2026-09-25";
export const SIM_HASH = `${SIM_VERSION}-${fnv1a(JSON.stringify(tuning)).toString(16)}`;

function fnv1a(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
